import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API_BASE, extractError } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Bot, Send, Plus, History, Mic, MicOff, Copy, RefreshCcw,
  Trash2, Sparkles, Loader2, ChevronLeft, Search, Save,
} from "lucide-react";

// Detect if text is primarily Arabic (has Arabic Unicode range chars).
// Used to auto-flip the direction per message so Arabic reads RTL and English reads LTR.
const AR_RE = /[\u0600-\u06FF]/;
function detectDir(text) {
  return AR_RE.test(text || "") ? "rtl" : "ltr";
}

/**
 * AI Chat page — ChatGPT-style, streams via SSE using fetch (Authorization header).
 * Persists last-viewed chat in localStorage so returning to the page resumes automatically.
 */
export default function AIChatPage() {
  const [params, setParams] = useSearchParams();
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyQuery, setHistoryQuery] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [loading, setLoading] = useState(true);
  const [micOn, setMicOn] = useState(false);
  const abortRef = useRef(null);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);

  // ---------------------------------------------------------- init
  useEffect(() => {
    (async () => {
      try {
        const [{ data: sug }] = await Promise.all([api.get("/ai-chat/suggestions")]);
        setSuggestions(sug.prompts || []);
        // Always preload chats list so the desktop sidebar and history search are ready
        reloadHistory().catch(() => {});
        const idFromUrl = params.get("chat");
        const idFromLS = localStorage.getItem("last_ai_chat");
        const targetId = idFromUrl || idFromLS;
        if (targetId) {
          try { await openChat(targetId); return; } catch { /* fallthrough */ }
        }
        // No prior chat — try loading last from server
        const { data } = await api.get("/ai-chat/chats/last");
        if (data.chat) await openChat(data.chat.id);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    reloadHistory().catch(() => {});
    // eslint-disable-next-line
  }, [historyQuery]);

  useEffect(() => {
    // auto-scroll on new message or streaming delta
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamingText]);

  // ---------------------------------------------------------- data
  const reloadHistory = async () => {
    const { data } = await api.get("/ai-chat/chats", { params: historyQuery ? { q: historyQuery } : {} });
    setChats(data);
  };

  const openChat = async (id) => {
    const { data } = await api.get(`/ai-chat/chats/${id}`);
    setChatId(data.chat.id);
    setMessages(data.messages || []);
    localStorage.setItem("last_ai_chat", data.chat.id);
    setParams({ chat: data.chat.id }, { replace: true });
    setHistoryOpen(false);
  };

  const newChat = async () => {
    const { data } = await api.post("/ai-chat/chats", {});
    setChatId(data.id);
    setMessages([]);
    setStreamingText("");
    localStorage.setItem("last_ai_chat", data.id);
    setParams({ chat: data.id }, { replace: true });
    setHistoryOpen(false);
    setInput("");
  };

  const deleteChat = async (id) => {
    if (!window.confirm("حذف هذه المحادثة نهائياً؟")) return;
    await api.delete(`/ai-chat/chats/${id}`);
    toast.success("تم الحذف");
    if (id === chatId) {
      setChatId(null); setMessages([]);
      localStorage.removeItem("last_ai_chat");
    }
    reloadHistory();
  };

  const renameChat = async (id, currentTitle) => {
    const t = window.prompt("اسم المحادثة:", currentTitle);
    if (!t || !t.trim()) return;
    await api.patch(`/ai-chat/chats/${id}`, { title: t.trim() });
    reloadHistory();
    if (id === chatId) {
      // refresh title in URL/state indirectly by reopening
      const { data } = await api.get(`/ai-chat/chats/${id}`);
      // no state slot for title in this page; history list refreshes above
    }
  };

  // ---------------------------------------------------------- streaming send
  const sendMessage = async (text) => {
    if (!text.trim() || streaming) return;
    let cid = chatId;
    if (!cid) {
      const { data } = await api.post("/ai-chat/chats", {});
      cid = data.id;
      setChatId(cid);
      localStorage.setItem("last_ai_chat", cid);
      setParams({ chat: cid }, { replace: true });
    }

    // Optimistically append user message
    const userMsg = { id: `tmp-${Date.now()}`, chat_id: cid, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setStreaming(true);
    setStreamingText("");

    try {
      const token = localStorage.getItem("access_token");
      const controller = new AbortController();
      abortRef.current = controller;
      const resp = await fetch(`${API_BASE}/ai-chat/chats/${cid}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: text }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const errText = await resp.text().catch(() => "");
        throw new Error(errText.includes("detail") ? JSON.parse(errText).detail : `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let acc = "";
      let assistantId = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const evt = JSON.parse(jsonStr);
            if (evt.delta) {
              acc += evt.delta;
              setStreamingText(acc);
            } else if (evt.done) {
              assistantId = evt.assistant_id;
            } else if (evt.error) {
              throw new Error(evt.error);
            }
          } catch (_) { /* ignore parse errors */ }
        }
      }

      // Persist the final assistant message locally
      if (acc) {
        setMessages((m) => [...m, {
          id: assistantId || `tmp-${Date.now()}`,
          chat_id: cid, role: "assistant", content: acc,
          created_at: new Date().toISOString(),
        }]);
      }
      setStreamingText("");
      // Reload sidebar so title auto-generation + preview show up in the history list
      reloadHistory().catch(() => {});
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(e.message || "فشل الإرسال");
      }
      setStreamingText("");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const stopStreaming = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const regenerate = async () => {
    if (streaming || !chatId) return;
    // Simply re-send last user message via the regenerate endpoint (which handles cleanup server-side)
    setStreaming(true);
    setStreamingText("");
    // Optimistically drop the last assistant message from UI
    setMessages((m) => {
      const idx = [...m].reverse().findIndex((x) => x.role === "assistant");
      if (idx === -1) return m;
      return m.slice(0, m.length - 1 - idx);
    });
    try {
      const token = localStorage.getItem("access_token");
      const controller = new AbortController();
      abortRef.current = controller;
      const resp = await fetch(`${API_BASE}/ai-chat/chats/${chatId}/regenerate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = ""; let acc = ""; let assistantId = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) { acc += evt.delta; setStreamingText(acc); }
            else if (evt.done) { assistantId = evt.assistant_id; }
          } catch {}
        }
      }
      if (acc) setMessages((m) => [...m, {
        id: assistantId || `tmp-${Date.now()}`, chat_id: chatId, role: "assistant",
        content: acc, created_at: new Date().toISOString(),
      }]);
      setStreamingText("");
    } catch (e) {
      toast.error(e.message || "فشل إعادة التوليد");
    } finally {
      setStreaming(false);
    }
  };

  // ---------------------------------------------------------- voice input (Web Speech API)
  const toggleMic = () => {
    if (micOn) {
      recognitionRef.current?.stop();
      setMicOn(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("متصفحك لا يدعم الإدخال الصوتي"); return; }
    const rec = new SR();
    rec.lang = "ar-SA";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      let final = "";
      for (const r of e.results) final += r[0].transcript;
      setInput(final);
    };
    rec.onerror = () => setMicOn(false);
    rec.onend = () => setMicOn(false);
    rec.start();
    recognitionRef.current = rec;
    setMicOn(true);
  };

  // ---------------------------------------------------------- render
  const showEmpty = !chatId || messages.length === 0;

  return (
    <div className="flex h-[calc(100dvh-11rem)] lg:h-[calc(100vh-9rem)] -mt-6 lg:-mt-10">
      {/* History sidebar (mobile: drawer, desktop: fixed) */}
      <HistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        chats={chats}
        onOpen={openChat}
        onDelete={deleteChat}
        onRename={renameChat}
        currentId={chatId}
        query={historyQuery}
        setQuery={setHistoryQuery}
      />

      {/* Main chat pane */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="flex items-center justify-between gap-2 border-b border-foreground/10 py-2 lg:py-3 px-1" data-testid="ai-chat-header">
          <Button variant="ghost" size="sm" onClick={newChat} className="gap-1.5 px-2 lg:px-3" data-testid="btn-new-chat" aria-label="محادثة جديدة">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">محادثة جديدة</span>
          </Button>
          <div className="flex items-center gap-1.5 text-sm text-foreground/70 min-w-0">
            <Bot className="h-4 w-4 text-purple-600 shrink-0" />
            <span className="font-display font-semibold truncate">مدرب فيصل الذكي</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)} className="gap-1.5 px-2 lg:px-3" data-testid="btn-open-history" aria-label="السجل">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">السجل</span>
          </Button>
        </header>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto py-4 space-y-4" data-testid="ai-chat-messages">
          {loading ? (
            <div className="text-center text-foreground/50 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : showEmpty ? (
            <EmptyState suggestions={suggestions} onPick={(t) => sendMessage(t)} />
          ) : (
            messages.map((m) => (
              <MessageBubble
                key={m.id}
                msg={m}
                onCopy={() => { navigator.clipboard.writeText(m.content); toast.success("تم النسخ"); }}
                onRegenerate={m.role === "assistant" ? regenerate : null}
              />
            ))
          )}
          {streaming && streamingText && (
            <MessageBubble msg={{ role: "assistant", content: streamingText, id: "streaming" }} isStreaming />
          )}
          {streaming && !streamingText && (
            <div className="flex items-center gap-2 text-sm text-foreground/60 py-2" data-testid="ai-chat-thinking">
              <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
              <span>مدرب فيصل يفكّر...</span>
            </div>
          )}
        </div>

        {/* Composer */}
        <Composer
          value={input}
          onChange={setInput}
          onSend={() => sendMessage(input)}
          onStop={stopStreaming}
          streaming={streaming}
          micOn={micOn}
          toggleMic={toggleMic}
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- pieces

function EmptyState({ suggestions, onPick }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-2 sm:px-4 text-center py-4" data-testid="ai-chat-empty">
      <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 grid place-items-center mb-3 sm:mb-4 shadow-lg">
        <Bot className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
      </div>
      <h2 className="text-lg sm:text-2xl font-display font-bold mb-1 sm:mb-2">أهلاً! أنا مدرب فيصل الذكي 👋</h2>
      <p className="text-foreground/60 text-xs sm:text-sm mb-4 sm:mb-6 max-w-md leading-relaxed">
        اسألني عن التمارين، الوصفات، خطط الأكل، أو أي شيء يخص لياقتك وصحتك.
      </p>
      <div className="grid gap-1.5 sm:gap-2 grid-cols-1 sm:grid-cols-2 max-w-xl w-full">
        {suggestions.map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s.text)}
            className="text-start p-2.5 sm:p-3 rounded-xl border border-foreground/10 bg-card hover:border-purple-300 hover:bg-purple-500/10 transition-colors text-xs sm:text-sm flex items-start gap-2 text-foreground"
            data-testid={`suggestion-${i}`}
          >
            <span className="text-base sm:text-lg">{s.icon}</span>
            <span className="flex-1 leading-relaxed">{s.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg, isStreaming, onCopy, onRegenerate }) {
  const isUser = msg.role === "user";
  const dir = detectDir(msg.content);
  const align = dir === "rtl" ? "text-right" : "text-left";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} px-1`} data-testid={`msg-${isUser ? "user" : "assistant"}-${msg.id}`}>
      <div className={`max-w-[85%] sm:max-w-[75%] ${isUser ? "" : "flex gap-2.5"}`}>
        {!isUser && (
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 grid place-items-center shrink-0">
            <Bot className="h-4 w-4 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div
            dir={dir}
            className={`rounded-2xl px-4 py-2.5 leading-relaxed text-sm break-words ${align} ${
              isUser ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-secondary/80 text-foreground rounded-tl-sm"
            }`}
          >
            {isUser ? (
              <span className="whitespace-pre-wrap">{msg.content}</span>
            ) : (
              <MarkdownBody content={msg.content} />
            )}
            {isStreaming && <span className="inline-block w-2 h-4 bg-current opacity-60 ms-0.5 animate-pulse align-middle" />}
          </div>
          {!isUser && !isStreaming && (
            <div className="flex items-center gap-1 mt-1 opacity-60 hover:opacity-100 transition-opacity">
              {onCopy && (
                <button onClick={onCopy} className="p-1 rounded hover:bg-foreground/5" data-testid={`btn-copy-${msg.id}`} aria-label="نسخ">
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
              {onRegenerate && (
                <button onClick={onRegenerate} className="p-1 rounded hover:bg-foreground/5" data-testid={`btn-regen-${msg.id}`} aria-label="إعادة توليد">
                  <RefreshCcw className="h-3.5 w-3.5" />
                </button>
              )}
              {looksLikeRecipe(msg.content) && (
                <SaveRecipeButton content={msg.content} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Lightweight markdown renderer scoped for chat bubbles.
// Styles headings smaller (chat bubble isn't a document) and keeps bold/lists tidy.
function MarkdownBody({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ node, ...p }) => <h3 className="font-display font-bold text-base mt-2 mb-1" {...p} />,
        h2: ({ node, ...p }) => <h4 className="font-display font-bold text-sm mt-2 mb-1" {...p} />,
        h3: ({ node, ...p }) => <h5 className="font-semibold text-sm mt-1.5 mb-0.5" {...p} />,
        p:  ({ node, ...p }) => <p className="my-1.5 leading-relaxed" {...p} />,
        strong: ({ node, ...p }) => <strong className="font-semibold text-current" {...p} />,
        em: ({ node, ...p }) => <em className="italic" {...p} />,
        ul: ({ node, ...p }) => <ul className="list-disc ps-5 my-1.5 space-y-0.5" {...p} />,
        ol: ({ node, ...p }) => <ol className="list-decimal ps-5 my-1.5 space-y-0.5" {...p} />,
        li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
        code: ({ node, inline, ...p }) => inline
          ? <code className="bg-foreground/10 rounded px-1 py-0.5 text-[0.85em]" {...p} />
          : <code className="block bg-foreground/10 rounded p-2 text-[0.85em] overflow-x-auto whitespace-pre" {...p} />,
        a: ({ node, ...p }) => <a className="underline text-primary" target="_blank" rel="noreferrer" {...p} />,
        blockquote: ({ node, ...p }) => <blockquote className="border-s-2 border-current/30 ps-2 my-1.5 opacity-90" {...p} />,
        hr: () => <hr className="my-2 border-current/20" />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function Composer({ value, onChange, onSend, onStop, streaming, micOn, toggleMic }) {
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  };
  return (
    <div className="border-t border-foreground/10 pt-2 pb-1 lg:pt-3 lg:pb-2 bg-background" data-testid="ai-chat-composer">
      <div className="flex items-end gap-2">
        <button
          onClick={toggleMic}
          className={`h-10 w-10 rounded-full grid place-items-center shrink-0 transition-colors ${
            micOn ? "bg-rose-500 text-white animate-pulse" : "bg-secondary hover:bg-secondary/80 text-foreground"
          }`}
          data-testid="btn-mic"
          aria-label="إدخال صوتي"
          disabled={streaming}
        >
          {micOn ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKey}
          dir={value ? detectDir(value) : undefined}
          placeholder="اكتب رسالتك..."
          rows={1}
          className="resize-none min-h-[40px] max-h-[160px] flex-1"
          data-testid="ai-chat-input"
          disabled={streaming}
        />
        {streaming ? (
          <Button onClick={onStop} variant="destructive" size="icon" className="shrink-0 h-10 w-10 rounded-full" data-testid="btn-stop">
            <span className="h-3 w-3 bg-white rounded-sm" />
          </Button>
        ) : (
          <Button onClick={onSend} size="icon" className="shrink-0 h-10 w-10 rounded-full" disabled={!value.trim()} data-testid="btn-send">
            <Send className="h-4 w-4 rtl:-scale-x-100" />
          </Button>
        )}
      </div>
      <div className="hidden lg:block text-[10px] text-foreground/40 mt-1.5 text-center">
        قد يخطئ الذكاء الاصطناعي — راجع المعلومات المهمة قبل تطبيقها.
      </div>
    </div>
  );
}

function HistoryPanel({ open, onClose, chats, onOpen, onDelete, onRename, currentId, query, setQuery }) {
  return (
    <>
      {/* Overlay when drawer is open (all screen sizes now behave like ChatGPT mobile) */}
      {open && <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} data-testid="history-overlay" />}
      {/* Drawer — slides in from the start side (right in RTL) */}
      <aside
        className={`fixed top-0 bottom-0 start-0 z-50 w-80 max-w-[85vw] bg-card border-e border-foreground/10 shadow-2xl transition-transform ${
          open ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"
        }`}
        data-testid="history-panel"
      >
        <div className="p-3 border-b border-foreground/10 flex items-center justify-between">
          <div className="font-display font-semibold text-sm">المحادثات السابقة</div>
          <button className="p-1" onClick={onClose} aria-label="إغلاق" data-testid="btn-close-history">
            <ChevronLeft className="h-4 w-4 rtl:-scale-x-100" />
          </button>
        </div>
        <div className="p-3">
          <div className="relative">
            <Search className="absolute top-2.5 start-2.5 h-3.5 w-3.5 text-foreground/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ابحث في المحادثات..."
              className="w-full text-sm ps-8 pe-2 py-2 rounded-lg border border-foreground/10 bg-white/60"
              data-testid="history-search"
            />
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(100vh-8rem)] px-2 pb-4 space-y-1">
          {chats.length === 0 && <div className="text-center text-xs text-foreground/40 py-6">لا محادثات سابقة</div>}
          {chats.map((c) => (
            <div
              key={c.id}
              className={`group flex items-start gap-2 rounded-lg p-2 cursor-pointer transition-colors ${
                c.id === currentId ? "bg-purple-50 border border-purple-200" : "hover:bg-secondary/60"
              }`}
              onClick={() => onOpen(c.id)}
              data-testid={`history-item-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm line-clamp-1">{c.title || "محادثة"}</div>
                <div className="text-[11px] text-foreground/50 line-clamp-1 mt-0.5">
                  {c.last_preview || "—"}
                </div>
                <div className="text-[10px] text-foreground/40 mt-0.5">{formatDate(c.updated_at)}</div>
              </div>
              <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); onRename(c.id, c.title); }}
                  className="p-1 rounded hover:bg-foreground/5"
                  data-testid={`btn-rename-${c.id}`}
                  aria-label="إعادة تسمية"
                >
                  <Sparkles className="h-3 w-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                  className="p-1 rounded hover:bg-rose-100 text-rose-600"
                  data-testid={`btn-delete-chat-${c.id}`}
                  aria-label="حذف"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}

function SaveRecipeButton({ content }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const doSave = async () => {
    setSaving(true);
    try {
      // Ask the AI extract endpoint to normalize this free-form text into a structured recipe.
      // Falls back to a simple "manual" save if extraction fails.
      const { data } = await api.post("/user-recipes/from-text", { free_text: content });
      toast.success(`تم حفظ الوصفة: ${data.title || "الوصفة"}`);
      setSaved(true);
    } catch (e) {
      toast.error(extractError(e, "تعذّر حفظ الوصفة"));
    } finally { setSaving(false); }
  };
  if (saved) return <span className="text-[10px] text-emerald-600 px-1">✓ محفوظة</span>;
  return (
    <button onClick={doSave} disabled={saving} className="p-1 rounded hover:bg-foreground/5" data-testid="btn-save-recipe" aria-label="حفظ الوصفة">
      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
    </button>
  );
}

// Heuristic — does this assistant message look like it contains a recipe?
function looksLikeRecipe(text) {
  if (!text || text.length < 100) return false;
  const t = text.toLowerCase();
  const hits = [
    /المكونات[:：]/, /مكونات[:：]/, /الخطوات[:：]/, /طريقة التحضير/, /السعرات/,
    /ingredients[:：]/, /steps[:：]/,
  ];
  return hits.some((rx) => rx.test(text));
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffH = diffMs / 3600e3;
  if (diffH < 24) return d.toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" });
  const diffD = diffH / 24;
  if (diffD < 7) return `${Math.floor(diffD)} يوم`;
  return d.toLocaleDateString("ar-EG", { day: "numeric", month: "short" });
}
